import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, CalendarX2, ClipboardCheck, Download, Inbox, Loader2, LogOut, Megaphone, Plus, Search, ShieldCheck, Ticket, Trash2, UserCog, UserRound, Users } from "lucide-react";
import { api, apiFile, formatApiError, useAuth } from "@/auth";
import { GROUP_OPTIONS } from "@/i18n";

const GROUP_CHOICES = ["all", ...GROUP_OPTIONS.map((o) => o.split(" · ")[0])];
const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none transition-colors duration-300 focus:border-accent";
const labelCls = "mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400";

const fmt = (iso) =>
  new Date(iso).toLocaleDateString("es-ES", { timeZone: "Atlantic/Canary", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function AdminPanel() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("inscripciones");
  const [inquiries, setInquiries] = useState([]);
  const [events, setEvents] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [notices, setNotices] = useState([]);
  const [nForm, setNForm] = useState({ title: "", body: "", groups: ["all"] });
  const [students, setStudents] = useState([]);
  const [users, setUsers] = useState([]);
  const [attendance, setAttendance] = useState({ sessions: [], summary: {} });
  const [uForm, setUForm] = useState(null);
  const [wk, setWk] = useState({ registrations: [], stats: { total: 0, single: 0, full: 0, session_first: 0, session_second: 0, paid: 0, pending: 0, rejected: 0, revenue_eur: 0 } });
  const [wkSearch, setWkSearch] = useState("");
  const [evForm, setEvForm] = useState({ title: "", description: "", date: "", groups: ["all"] });
  const [cForm, setCForm] = useState({ group_code: "all", date: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/inquiries").then((r) => setInquiries(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/events").then((r) => setEvents(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/cancellations").then((r) => setCancellations(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/notices").then((r) => setNotices(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/students").then((r) => setStudents(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/attendance").then((r) => setAttendance(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/workshop").then((r) => setWk(r.data)).catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(load, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
    navigate("/");
  };

  const toggleGroup = (g) =>
    setEvForm((f) => {
      let next = f.groups.includes(g) ? f.groups.filter((x) => x !== g) : [...f.groups, g];
      if (g === "all" && next.includes("all")) return { ...f, groups: ["all"] };
      return { ...f, groups: next.filter((x) => x !== "all") };
    });

  const createEvent = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/events", { ...evForm, date: new Date(evForm.date).toISOString() });
      toast.success("Evento publicado");
      setEvForm({ title: "", description: "", date: "", groups: ["all"] });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const createCancellation = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/cancellations", { ...cForm, date: new Date(cForm.date).toISOString() });
      toast.success("Aviso de cancelación publicado");
      setCForm({ group_code: "all", date: "", reason: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const removeEvent = async (id) => {
    await api.delete(`/admin/events/${id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const removeCancellation = async (id) => {
    await api.delete(`/admin/cancellations/${id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const toggleNoticeGroup = (g) =>
    setNForm((f) => {
      let next = f.groups.includes(g) ? f.groups.filter((x) => x !== g) : [...f.groups, g];
      if (g === "all" && next.includes("all")) return { ...f, groups: ["all"] };
      return { ...f, groups: next.filter((x) => x !== "all") };
    });

  const createNotice = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/notices", nForm);
      toast.success("Comunicado publicado: avisaremos por email a la clase");
      setNForm({ title: "", body: "", groups: ["all"] });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const removeNotice = async (id) => {
    await api.delete(`/admin/notices/${id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const approveInquiry = async (id) => {
    await api
      .post(`/admin/inquiries/${id}/approve`)
      .then(() => toast.success("Matrícula aprobada: el alumno ya puede registrarse"))
      .catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const patchInquiry = async (id, body) => {
    await api.patch(`/admin/inquiries/${id}`, body).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const importRoster = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/import-roster");
      toast.success(
        data.inserted > 0
          ? `Listado cargado: ${data.inserted} alumnos añadidos (${data.total_roster} en total)`
          : `El listado ya estaba cargado (${data.total_roster} alumnos). No se duplicó nada.`
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (uForm.id) {
        await api.patch(`/admin/users/${uForm.id}`, {
          name: uForm.name,
          role: uForm.role,
          ...(uForm.role === "student" ? { group_code: uForm.group_code } : {}),
          ...(uForm.role === "teacher" ? { assigned_groups: uForm.assigned_groups } : {}),
          ...(uForm.new_password ? { new_password: uForm.new_password } : {}),
        });
        toast.success("Usuario actualizado");
      } else {
        await api.post("/admin/users", uForm);
        toast.success("Usuario creado");
      }
      setUForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`¿Dar de baja a ${u.name}? Perderá el acceso a la plataforma.`)) return;
    await api.delete(`/admin/users/${u.id}`).then(() => toast.success("Usuario dado de baja")).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const wkFiltered = wk.registrations.filter((r) =>
    `${r.first_name} ${r.last_name} ${r.email} ${r.reference}`.toLowerCase().includes(wkSearch.toLowerCase())
  );

  const setWkStatus = async (id, status) => {
    await api.patch(`/admin/workshop/${id}`, { payment_status: status }).then(() => toast.success(`Inscripción marcada como ${status}`)).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const exportWorkshopCSV = () => {
    const rows = [
      ["Referencia", "Nombre", "Apellidos", "Fecha nacimiento", "Email", "Teléfono", "Modalidad", "Sesiones", "Importe", "Estado pago", "Fecha inscripción", "Menor", "Tutor", "Email tutor", "Teléfono tutor", "Uso de imágenes"],
      ...wk.registrations.map((r) => [
        r.reference, r.first_name, r.last_name, r.birth_date, r.email, r.phone,
        r.modality === "full" ? "Full Experience" : "Single Workshop",
        r.modality === "full" ? "1ª + 2ª" : r.session === "first" ? "1ª" : "2ª",
        `${r.amount_eur}€`, r.payment_status, r.created_at?.slice(0, 10) || "",
        r.is_minor ? "Sí" : "No", r.guardian_name || "", r.guardian_email || "", r.guardian_phone || "",
        r.consent_images ? "Sí" : "No",
      ]),
    ];
    const csv = "\uFEFF" + rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "workshop-jay-kim-inscripciones.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: "inscripciones", label: `Inscripciones (${inquiries.length})`, icon: Inbox, test: "admin-tab-inquiries" },
    { id: "workshop", label: "Workshop JAY KIM", icon: Ticket, test: "admin-tab-workshop" },
    { id: "alumnado", label: "Alumnado", icon: Users, test: "admin-tab-students" },
    { id: "asistencia", label: "Asistencia", icon: ClipboardCheck, test: "admin-tab-attendance" },
    { id: "usuarios", label: "Usuarios", icon: UserCog, test: "admin-tab-users" },
    { id: "eventos", label: "Eventos", icon: CalendarDays, test: "admin-tab-events" },
    { id: "cancelaciones", label: "Cancelaciones", icon: CalendarX2, test: "admin-tab-cancellations" },
    { id: "mensajes", label: "Mensajes", icon: Megaphone, test: "admin-tab-notices" },
  ];

  return (
    <div className="mx-auto max-w-7xl px-5 pb-24 pt-28 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-2 font-mono text-xs font-bold uppercase tracking-[0.3em] text-accent">Panel privado</p>
          <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">Gestión K-house</h1>
        </div>
        <button
          data-testid="admin-logout"
          onClick={logout}
          className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-zinc-300 transition-all duration-300 hover:border-accent hover:text-accent"
        >
          <LogOut size={14} /> Salir
        </button>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            data-testid={tb.test}
            onClick={() => setTab(tb.id)}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] transition-all duration-300 ${
              tab === tb.id ? "bg-accent text-white" : "border border-white/15 text-zinc-400 hover:text-white"
            }`}
          >
            <tb.icon size={14} />
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "inscripciones" && (
        <div className="mt-8 overflow-hidden rounded-xl border border-white/10 bg-card">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm" data-testid="admin-inquiries-table">
              <thead>
                <tr className="border-b border-white/10 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">
                  <th className="px-5 py-4">Fecha</th>
                  <th className="px-5 py-4">Alumno/a</th>
                  <th className="px-5 py-4">Grupo</th>
                  <th className="px-5 py-4">Teléfono</th>
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Comentarios</th>
                  <th className="px-5 py-4">Email ✓</th>
                  <th className="px-5 py-4">Estado</th>
                </tr>
              </thead>
              <tbody>
                {inquiries.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-10 text-center text-zinc-400">
                      Todavía no hay inscripciones.
                    </td>
                  </tr>
                )}
                {inquiries.map((q) => (
                  <tr key={q.id} data-testid={`admin-inquiry-row-${q.id}`} className="border-b border-white/5 transition-colors hover:bg-white/[0.03]">
                    <td className="px-5 py-4 font-mono text-xs text-zinc-400">{fmt(q.created_at)}</td>
                    <td className="px-5 py-4 font-bold text-white">
                      {q.student_name}
                      {q.guardian_name && <span className="block text-xs font-normal text-zinc-500">Tutor: {q.guardian_name}</span>}
                    </td>
                    <td className="px-5 py-4"><span className="rounded bg-accent/15 px-2 py-1 font-mono text-xs text-accent">{q.selected_group.split(" · ")[0]}</span></td>
                    <td className="px-5 py-4 text-zinc-300">{q.phone}</td>
                    <td className="px-5 py-4 text-zinc-300">{q.email}</td>
                    <td className="max-w-[220px] px-5 py-4 text-zinc-400">{q.comments || "—"}</td>
                    <td className="px-5 py-4 font-mono text-xs">{q.emailed ? <span className="text-emerald-400">Enviado</span> : <span className="text-zinc-500">Pendiente</span>}</td>
                    <td className="px-5 py-4">
                      {q.status === "approved" ? (
                        <span data-testid={`inquiry-status-approved-${q.id}`} className="rounded-full border border-emerald-500/50 bg-emerald-500/10 px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-emerald-400">
                          Aprobada
                        </span>
                      ) : (
                        <button
                          data-testid={`admin-approve-${q.id}`}
                          onClick={() => approveInquiry(q.id)}
                          className="rounded-full bg-accent px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-white transition-all duration-300 hover:bg-accent-hover"
                        >
                          Aprobar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "eventos" && (
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <form data-testid="admin-event-form" onSubmit={createEvent} className="space-y-4 rounded-xl border border-white/10 bg-card p-6 lg:col-span-2">
            <h2 className="flex items-center gap-2 font-display text-sm font-extrabold uppercase">
              <Plus size={15} className="text-accent" /> Nuevo evento
            </h2>
            <div>
              <label htmlFor="ev-title" className={labelCls}>Título *</label>
              <input id="ev-title" data-testid="admin-event-title" required value={evForm.title} onChange={(e) => setEvForm((f) => ({ ...f, title: e.target.value }))} className={inputCls} placeholder="Ej. Ensayo general concurso" />
            </div>
            <div>
              <label htmlFor="ev-date" className={labelCls}>Fecha y hora *</label>
              <input id="ev-date" data-testid="admin-event-date" type="datetime-local" required value={evForm.date} onChange={(e) => setEvForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label htmlFor="ev-desc" className={labelCls}>Descripción</label>
              <textarea id="ev-desc" data-testid="admin-event-description" rows={3} value={evForm.description} onChange={(e) => setEvForm((f) => ({ ...f, description: e.target.value }))} className={`${inputCls} resize-none`} />
            </div>
            <div>
              <p className={labelCls}>Grupos destinatarios</p>
              <div className="flex flex-wrap gap-2">
                {GROUP_CHOICES.map((g) => (
                  <button
                    type="button"
                    key={g}
                    data-testid={`admin-event-group-${g.replace(".", "-").toLowerCase()}`}
                    onClick={() => toggleGroup(g)}
                    className={`rounded-full px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.1em] transition-all duration-300 ${
                      evForm.groups.includes(g) ? "bg-accent text-white" : "border border-white/15 text-zinc-400"
                    }`}
                  >
                    {g === "all" ? "Todos" : g}
                  </button>
                ))}
              </div>
            </div>
            <button type="submit" data-testid="admin-event-submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-accent-hover disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Publicar evento
            </button>
          </form>

          <div className="space-y-3 rounded-xl border border-white/10 bg-card p-6 lg:col-span-3" data-testid="admin-events-list">
            {events.length === 0 && <p className="py-8 text-center text-sm text-zinc-400">No hay eventos creados todavía.</p>}
            {events.map((ev) => (
              <div key={ev.id} className="flex items-start justify-between gap-4 rounded-lg border border-white/10 bg-panel p-4">
                <div>
                  <p className="font-display text-sm font-extrabold uppercase">{ev.title}</p>
                  <p className="mt-1 flex items-center gap-1.5 font-mono text-xs text-accent"><CalendarDays size={12} /> {fmt(ev.date)}</p>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">
                    {ev.groups.includes("all") ? "Todos los grupos" : ev.groups.join(" · ")}
                  </p>
                  {ev.description && <p className="mt-2 text-sm text-zinc-400">{ev.description}</p>}
                </div>
                <button data-testid={`admin-event-delete-${ev.id}`} onClick={() => removeEvent(ev.id)} aria-label="Eliminar evento" className="rounded-full border border-white/15 p-2 text-zinc-400 transition-all duration-300 hover:border-accent hover:text-accent">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "workshop" && (
        <div className="mt-8 space-y-6">
          <h2 className="font-display text-lg font-extrabold uppercase tracking-tight text-accent">
            Workshop Registration Management
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="workshop-stats">
            {[
              { label: "Inscritos", value: wk.stats.total },
              { label: "Single Workshop", value: wk.stats.single },
              { label: "Full Experience", value: wk.stats.full },
              { label: "1ª sesión", value: wk.stats.session_first },
              { label: "2ª sesión", value: wk.stats.session_second },
              { label: "Pagos confirmados", value: wk.stats.paid },
              { label: "Pagos pendientes", value: wk.stats.pending },
              { label: "Recaudación confirmada", value: `${wk.stats.revenue_eur}€` },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-white/10 bg-card p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{s.label}</p>
                <p className="mt-1.5 font-display text-2xl font-extrabold text-white">{s.value}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <input
              data-testid="workshop-search"
              value={wkSearch}
              onChange={(e) => setWkSearch(e.target.value)}
              placeholder="Buscar por nombre, apellidos o email…"
              className="max-w-xs rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder-zinc-500 outline-none focus:border-accent"
            />
            <button
              data-testid="workshop-export-csv"
              onClick={exportWorkshopCSV}
              className="flex items-center gap-2 rounded-full border border-accent/60 bg-accent/10 px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-accent transition-all duration-300 hover:bg-accent hover:text-white"
            >
              <Download size={14} /> Exportar CSV
            </button>
          </div>

          <div className="overflow-hidden rounded-xl border border-white/10 bg-card">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-left text-sm" data-testid="workshop-table">
                <thead>
                  <tr className="border-b border-white/10 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">
                    <th className="px-4 py-4">Referencia</th>
                    <th className="px-4 py-4">Participante</th>
                    <th className="px-4 py-4">Nacimiento</th>
                    <th className="px-4 py-4">Contacto</th>
                    <th className="px-4 py-4">Modalidad</th>
                    <th className="px-4 py-4">Sesiones</th>
                    <th className="px-4 py-4">Estado pago</th>
                    <th className="px-4 py-4">Conciliar</th>
                  </tr>
                </thead>
                <tbody>
                  {wkFiltered.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-zinc-400">
                        Todavía no hay inscripciones al workshop.
                      </td>
                    </tr>
                  )}
                  {wkFiltered.map((r) => (
                    <tr key={r.reference} data-testid={`workshop-row-${r.reference}`} className="border-b border-white/5 transition-colors hover:bg-white/[0.03]">
                      <td className="px-4 py-4 font-mono text-xs font-bold text-accent">{r.reference}</td>
                      <td className="px-4 py-4 font-bold text-white">
                        {r.first_name} {r.last_name}
                        {r.is_minor && <span className="block text-[10px] font-normal uppercase tracking-widest text-amber-300">Menor · {r.guardian_name}</span>}
                      </td>
                      <td className="px-4 py-4 font-mono text-xs text-zinc-400">{r.birth_date}</td>
                      <td className="px-4 py-4 text-zinc-300">
                        {r.email}
                        <span className="block text-xs text-zinc-500">{r.phone}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`rounded px-2 py-1 font-mono text-xs ${r.modality === "full" ? "bg-accent/20 text-accent" : "bg-white/10 text-zinc-300"}`}>
                          {r.modality === "full" ? "FULL 35€" : "SINGLE 20€"}
                        </span>
                      </td>
                      <td className="px-4 py-4 font-mono text-xs text-zinc-300">
                        {r.modality === "full" ? "1ª + 2ª" : r.session === "first" ? "1ª (15:30)" : "2ª (18:00)"}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.1em] ${
                          r.payment_status === "pagada" ? "bg-emerald-500/15 text-emerald-400" : r.payment_status === "rechazada" ? "bg-red-500/15 text-red-400" : "bg-amber-400/15 text-amber-300"
                        }`}>
                          {r.payment_status}
                        </span>
                        {r.paid_at && <span className="block text-[10px] text-zinc-500">{r.paid_at.slice(0, 10)}</span>}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-1.5">
                          {r.payment_status !== "pagada" && (
                            <button data-testid={`workshop-mark-paid-${r.reference}`} onClick={() => setWkStatus(r.reference, "pagada")} className="rounded-full bg-emerald-500/15 px-3 py-1.5 font-mono text-[10px] font-bold uppercase text-emerald-400 transition-colors hover:bg-emerald-500 hover:text-white">
                              Pagada
                            </button>
                          )}
                          {r.payment_status !== "rechazada" && (
                            <button data-testid={`workshop-mark-rejected-${r.reference}`} onClick={() => setWkStatus(r.reference, "rechazada")} className="rounded-full bg-red-500/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase text-red-400 transition-colors hover:bg-red-500 hover:text-white">
                              Rechazada
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === "alumnado" && (
        <div className="mt-8 overflow-hidden rounded-xl border border-white/10 bg-card">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 p-5">
            <p className="max-w-md text-sm text-zinc-400">Alumnado matriculado por clase. Cambia el grupo con el desplegable o desmatricula a quien se vaya.</p>
            <button
              data-testid="admin-import-roster"
              onClick={importRoster}
              disabled={busy}
              className="flex items-center gap-2 rounded-full border border-accent/60 bg-accent/10 px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-accent transition-all duration-300 hover:bg-accent hover:text-white disabled:opacity-60"
            >
              <Download size={14} /> Cargar listado inicial (94)
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm" data-testid="admin-students-table">
              <thead>
                <tr className="border-b border-white/10 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">
                  <th className="px-5 py-4">Alumno/a</th>
                  <th className="px-5 py-4">Grupo (clase)</th>
                  <th className="px-5 py-4">Contacto</th>
                  <th className="px-5 py-4">Matrícula</th>
                </tr>
              </thead>
              <tbody>
                {students.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-5 py-10 text-center text-zinc-400">
                      Sin matrículas aprobadas todavía.
                    </td>
                  </tr>
                )}
                {students.map((s) => (
                  <tr key={s.id} data-testid={`admin-student-row-${s.id}`} className="border-b border-white/5 transition-colors hover:bg-white/[0.03]">
                    <td className="px-5 py-4 font-bold text-white">
                      {s.student_name}
                      {s.guardian_name && <span className="block text-xs font-normal text-zinc-500">Tutor: {s.guardian_name}</span>}
                    </td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`admin-student-group-${s.id}`}
                        value={s.selected_group}
                        onChange={(e) => patchInquiry(s.id, { selected_group: e.target.value })}
                        className="appearance-none rounded border border-white/15 bg-panel px-3 py-1.5 font-mono text-xs text-white outline-none focus:border-accent"
                      >
                        {GROUP_OPTIONS.map((o) => (
                          <option key={o} value={o} className="bg-panel">{o}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4 text-zinc-300">
                      {s.phone}
                      <span className="block text-xs text-zinc-500">{s.email}</span>
                    </td>
                    <td className="px-5 py-4">
                      {s.archived ? (
                        <button data-testid={`admin-reenroll-${s.id}`} onClick={() => patchInquiry(s.id, { archived: false })} className="font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-accent hover:underline">
                          Reactivar
                        </button>
                      ) : (
                        <button data-testid={`admin-unenroll-${s.id}`} onClick={() => patchInquiry(s.id, { archived: true })} className="font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-400 transition-colors hover:text-accent">
                          Desmatricular
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "asistencia" && (
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-white/10 bg-card p-6" data-testid="admin-attendance-summary">
            <h2 className="font-display text-sm font-extrabold uppercase">Resumen por alumno</h2>
            {Object.keys(attendance.summary).length === 0 && <p className="py-8 text-center text-sm text-zinc-400">Sin registros de asistencia todavía.</p>}
            <div className="mt-4 space-y-2">
              {Object.entries(attendance.summary).map(([email, s]) => (
                <div key={email} data-testid={`attendance-summary-${email.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-panel px-4 py-3">
                  <div>
                    <p className="text-sm font-bold text-white">{s.name}</p>
                    {s.notes.length > 0 && <p className="text-xs text-zinc-500">{s.notes[s.notes.length - 1]}</p>}
                  </div>
                  <span className="font-mono text-xs">
                    <span className="text-emerald-400">{s.presente}P</span> · <span className="text-accent">{s.falta}F</span> · <span className="text-amber-300">{s.retraso}R</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-card p-6" data-testid="admin-attendance-sessions">
            <h2 className="font-display text-sm font-extrabold uppercase">Sesiones registradas</h2>
            {attendance.sessions.length === 0 && <p className="py-8 text-center text-sm text-zinc-400">Sin sesiones todavía.</p>}
            <div className="mt-4 space-y-2">
              {attendance.sessions.slice(0, 20).map((h) => (
                <details key={h.id} className="rounded-lg border border-white/10 bg-panel p-4">
                  <summary className="flex cursor-pointer items-center justify-between gap-2 font-mono text-xs uppercase tracking-[0.15em] text-zinc-300">
                    <span>{h.date} · {h.group_code}</span>
                    <span className="text-zinc-500">{h.teacher_name || "—"}</span>
                  </summary>
                  <div className="mt-3 space-y-1 border-t border-white/10 pt-3">
                    {(h.records || []).map((r) => (
                      <p key={r.email} className="text-xs text-zinc-300">
                        {r.name} — {r.status}{r.note ? ` (${r.note})` : ""}
                      </p>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "usuarios" && (
        <div className="mt-8">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="admin-profile-stats">
            {[
              { label: "Perfiles con acceso", count: users.length, icon: UserCog },
              { label: "Alumnado registrado", count: users.filter((u) => u.role === "student").length, icon: Users },
              { label: "Profesoras", count: users.filter((u) => u.role === "teacher").length, icon: UserRound },
              { label: "Gestión", count: users.filter((u) => u.role === "admin").length, icon: ShieldCheck },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-white/10 bg-card p-5">
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                  <s.icon size={13} className="text-accent" /> {s.label}
                </p>
                <p className="mt-2 font-display text-3xl font-extrabold text-white">{s.count}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <form
            data-testid="admin-user-form"
            onSubmit={saveUser}
            key={uForm?.id || "new"}
            className="space-y-4 rounded-xl border border-white/10 bg-card p-6 lg:col-span-2"
          >
            <h2 className="flex items-center gap-2 font-display text-sm font-extrabold uppercase">
              <UserCog size={15} className="text-accent" /> {uForm?.id ? "Editar usuario" : "Nuevo usuario"}
            </h2>
            <div>
              <label htmlFor="u-name" className={labelCls}>Nombre *</label>
              <input id="u-name" data-testid="admin-user-name" required value={uForm?.name || ""} onChange={(e) => setUForm((f) => ({ ...(f || {}), name: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label htmlFor="u-email" className={labelCls}>Email *</label>
              <input id="u-email" data-testid="admin-user-email" type="email" required disabled={!!uForm?.id} value={uForm?.email || ""} onChange={(e) => setUForm((f) => ({ ...(f || {}), email: e.target.value }))} className={`${inputCls} disabled:opacity-50`} />
            </div>
            <div>
              <label htmlFor="u-password" className={labelCls}>{uForm?.id ? "Nueva contraseña (opcional)" : "Contraseña *"}</label>
              <input id="u-password" data-testid="admin-user-password" type="password" required={!uForm?.id} minLength={uForm?.id ? undefined : 6} value={uForm?.new_password || ""} onChange={(e) => setUForm((f) => ({ ...(f || {}), new_password: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label htmlFor="u-role" className={labelCls}>Rol *</label>
              <select id="u-role" data-testid="admin-user-role" required value={uForm?.role || "teacher"} onChange={(e) => setUForm((f) => ({ ...(f || {}), role: e.target.value }))} className={`${inputCls} appearance-none`}>
                <option value="teacher" className="bg-panel">Profesora</option>
                <option value="student" className="bg-panel">Alumno/a</option>
                <option value="admin" className="bg-panel">Gestión K-house</option>
              </select>
            </div>
            {uForm?.role === "student" && (
              <div>
                <label htmlFor="u-group" className={labelCls}>Grupo del alumno/a *</label>
                <select id="u-group" data-testid="admin-user-group" required value={uForm?.group_code || ""} onChange={(e) => setUForm((f) => ({ ...(f || {}), group_code: e.target.value }))} className={`${inputCls} appearance-none`}>
                  <option value="" disabled className="bg-panel">Selecciona un grupo</option>
                  {GROUP_OPTIONS.map((o) => (
                    <option key={o} value={o} className="bg-panel">{o}</option>
                  ))}
                </select>
              </div>
            )}
            {uForm?.role === "teacher" && (
              <div>
                <p className={labelCls}>Clases asignadas</p>
                <div className="flex flex-wrap gap-2">
                  {GROUP_CHOICES.filter((g) => g !== "all").map((g) => (
                    <button
                      type="button"
                      key={g}
                      data-testid={`admin-user-group-${g.replace(".", "-").toLowerCase()}`}
                      onClick={() => setUForm((f) => {
                        const cur = f?.assigned_groups || [];
                        return { ...(f || {}), assigned_groups: cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g] };
                      })}
                      className={`rounded-full px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.1em] transition-all duration-300 ${
                        (uForm?.assigned_groups || []).includes(g) ? "bg-accent text-white" : "border border-white/15 text-zinc-400"
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-3">
              <button type="submit" data-testid="admin-user-submit" disabled={busy} className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-accent px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-accent-hover disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} {uForm?.id ? "Guardar cambios" : "Crear usuario"}
              </button>
              {uForm?.id && (
                <button type="button" data-testid="admin-user-cancel" onClick={() => setUForm(null)} className="rounded-full border border-white/15 px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-zinc-400 transition-colors hover:text-white">
                  Cancelar
                </button>
              )}
            </div>
          </form>

          <div className="space-y-3 rounded-xl border border-white/10 bg-card p-6 lg:col-span-3" data-testid="admin-users-list">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
              Todas las cuentas creadas con usuario y contraseña (alumnado, profesoras y gestión)
            </p>
            {users.length === 0 && <p className="py-8 text-center text-sm text-zinc-400">No hay cuentas creadas todavía.</p>}
            {users.map((u) => (
              <div key={u.id} data-testid={`admin-user-row-${u.id}`} className="flex items-start justify-between gap-4 rounded-lg border border-white/10 bg-panel p-4">
                <div className="flex items-start gap-3">
                  {u.photo_url ? (
                    <img src={apiFile(u.photo_url)} alt="" className="h-10 w-10 shrink-0 rounded-full border border-white/15 object-cover" />
                  ) : (
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/15 font-display text-xs font-extrabold text-accent">
                      {u.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                    </span>
                  )}
                  <div>
                    <p className="font-display text-sm font-extrabold uppercase text-white">{u.name}</p>
                    <p className="font-mono text-xs text-zinc-400">{u.email}</p>
                    {u.created_at && (
                      <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-600">
                        En la web desde {new Date(u.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}
                      </p>
                    )}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.1em] ${u.role === "admin" ? "bg-accent/20 text-accent" : u.role === "teacher" ? "bg-white/10 text-zinc-300" : "bg-white/10 text-zinc-300"}`}>
                      {{ teacher: "Profesora", student: "Alumno/a", admin: "Gestión" }[u.role] || u.role}
                    </span>
                    {u.role === "student" && u.group_code && <span className="rounded-full bg-accent/15 px-2.5 py-1 font-mono text-[10px] font-bold text-accent">{u.group_code}</span>}
                    {u.role === "teacher" && (u.assigned_groups || []).map((g) => (
                      <span key={g} className="rounded-full bg-white/10 px-2.5 py-1 font-mono text-[10px] font-bold text-zinc-300">{g}</span>
                    ))}
                  </div>
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    data-testid={`admin-user-edit-${u.id}`}
                    onClick={() => setUForm({ id: u.id, name: u.name, email: u.email, role: u.role, group_code: u.group_code || "", assigned_groups: u.assigned_groups || [], new_password: "" })}
                    className="rounded-full border border-white/15 px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-300 transition-colors hover:border-accent hover:text-accent"
                  >
                    Editar
                  </button>
                  <button
                    data-testid={`admin-user-delete-${u.id}`}
                    onClick={() => deleteUser(u)}
                    className="flex items-center gap-1 rounded-full border border-white/15 px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-400 transition-colors hover:border-accent hover:text-accent"
                  >
                    <Trash2 size={12} /> Baja
                  </button>
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>
      )}

      {tab === "mensajes" && (
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <form data-testid="admin-notice-form" onSubmit={createNotice} className="space-y-4 rounded-xl border border-white/10 bg-card p-6 lg:col-span-2">
            <h2 className="flex items-center gap-2 font-display text-sm font-extrabold uppercase">
              <Megaphone size={15} className="text-accent" /> Nuevo mensaje
            </h2>
            <div>
              <label htmlFor="nt-title" className={labelCls}>Título *</label>
              <input id="nt-title" data-testid="admin-notice-title" required value={nForm.title} onChange={(e) => setNForm((f) => ({ ...f, title: e.target.value }))} className={inputCls} placeholder="Ej. Recordatorio: traer zapatillas" />
            </div>
            <div>
              <label htmlFor="nt-body" className={labelCls}>Mensaje</label>
              <textarea id="nt-body" data-testid="admin-notice-body" rows={4} value={nForm.body} onChange={(e) => setNForm((f) => ({ ...f, body: e.target.value }))} className={`${inputCls} resize-none`} />
            </div>
            <div>
              <p className={labelCls}>Grupos destinatarios</p>
              <div className="flex flex-wrap gap-2">
                {GROUP_CHOICES.map((g) => (
                  <button
                    type="button"
                    key={g}
                    data-testid={`admin-notice-group-${g.replace(".", "-").toLowerCase()}`}
                    onClick={() => toggleNoticeGroup(g)}
                    className={`rounded-full px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.1em] transition-all duration-300 ${
                      nForm.groups.includes(g) ? "bg-accent text-white" : "border border-white/15 text-zinc-400"
                    }`}
                  >
                    {g === "all" ? "Todos" : g}
                  </button>
                ))}
              </div>
            </div>
            <button type="submit" data-testid="admin-notice-submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-accent-hover disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Publicar mensaje
            </button>
          </form>

          <div className="space-y-3 rounded-xl border border-white/10 bg-card p-6 lg:col-span-3" data-testid="admin-notices-list">
            {notices.length === 0 && <p className="py-8 text-center text-sm text-zinc-400">No hay mensajes publicados todavía.</p>}
            {notices.map((n) => (
              <div key={n.id} className="flex items-start justify-between gap-4 rounded-lg border border-white/10 bg-panel p-4">
                <div>
                  <p className="font-display text-sm font-extrabold uppercase">{n.title}</p>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">
                    {n.created_at?.slice(0, 10)} · {n.groups.includes("all") ? "Todos los grupos" : n.groups.join(" · ")}
                  </p>
                  {n.body && <p className="mt-2 text-sm text-zinc-400">{n.body}</p>}
                </div>
                <button data-testid={`admin-notice-delete-${n.id}`} onClick={() => removeNotice(n.id)} aria-label="Eliminar mensaje" className="rounded-full border border-white/15 p-2 text-zinc-400 transition-all duration-300 hover:border-accent hover:text-accent">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "cancelaciones" && (
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <form data-testid="admin-cancellation-form" onSubmit={createCancellation} className="space-y-4 rounded-xl border border-white/10 bg-card p-6 lg:col-span-2">
            <h2 className="flex items-center gap-2 font-display text-sm font-extrabold uppercase">
              <CalendarX2 size={15} className="text-accent" /> Nueva cancelación
            </h2>
            <div>
              <label htmlFor="cx-group" className={labelCls}>Grupo *</label>
              <select id="cx-group" data-testid="admin-cancellation-group" value={cForm.group_code} onChange={(e) => setCForm((f) => ({ ...f, group_code: e.target.value }))} className={`${inputCls} appearance-none`}>
                <option value="all" className="bg-panel">Todos los grupos</option>
                {GROUP_OPTIONS.map((o) => (
                  <option key={o} value={o.split(" · ")[0]} className="bg-panel">{o}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="cx-date" className={labelCls}>Fecha y hora de la clase cancelada *</label>
              <input id="cx-date" data-testid="admin-cancellation-date" type="datetime-local" required value={cForm.date} onChange={(e) => setCForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label htmlFor="cx-reason" className={labelCls}>Motivo</label>
              <textarea id="cx-reason" data-testid="admin-cancellation-reason" rows={3} value={cForm.reason} onChange={(e) => setCForm((f) => ({ ...f, reason: e.target.value }))} className={`${inputCls} resize-none`} placeholder="Ej. Por mal tiempo…" />
            </div>
            <button type="submit" data-testid="admin-cancellation-submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-accent-hover disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Publicar aviso
            </button>
          </form>

          <div className="space-y-3 rounded-xl border border-white/10 bg-card p-6 lg:col-span-3" data-testid="admin-cancellations-list">
            {cancellations.length === 0 && <p className="py-8 text-center text-sm text-zinc-400">No hay avisos de cancelación.</p>}
            {cancellations.map((c) => (
              <div key={c.id} className="flex items-start justify-between gap-4 rounded-lg border border-accent/30 bg-panel p-4">
                <div>
                  <p className="font-display text-sm font-extrabold uppercase text-white">
                    {c.group_code === "all" ? "Todos los grupos" : `Grupo ${c.group_code}`}
                  </p>
                  <p className="mt-1 font-mono text-xs text-accent">{fmt(c.date)}</p>
                  {c.reason && <p className="mt-2 text-sm text-zinc-400">{c.reason}</p>}
                  <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">
                    {c.confirmed_count} alumno(s) han confirmado
                  </p>
                </div>
                <button data-testid={`admin-cancellation-delete-${c.id}`} onClick={() => removeCancellation(c.id)} aria-label="Eliminar aviso" className="rounded-full border border-white/15 p-2 text-zinc-400 transition-all duration-300 hover:border-accent hover:text-accent">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
